import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { queryKeys } from '../services/query/queryKeys';
import { appwriteQueryFns } from '../services/query/appwriteQueryFns';
import { getPreferredStatus } from '../utils/statusMapping';
import { applyPendingIsrWrites } from '../utils/isrPendingWrites';

/**
 * Read this lead's status out of the per-campaign LIST cache
 * (`leads.byCampaign`). That cache is the reliable carrier of an optimistic
 * status update: `useISRLeadMutation` writes the user's just-applied status
 * straight into it, and `useLeadDetail` never refetches it — so while the
 * detail query's own refetch is busy overwriting `leads.detail` with the
 * server's lagging value (~15s behind while n8n processes the write), the list
 * cache still holds the truth. ISR is the ONLY view that mutates status, so
 * the optimistic value IS the source of truth, not a guess.
 *
 * Returns `undefined` when the list cache is cold (no data yet) so callers can
 * fall back to the server status untouched.
 */
const readListLeadStatus = (
  queryClient: QueryClient,
  campaignId: string,
  leadId: string,
): { status?: string; salesSubstatus?: string | null } | undefined => {
  // Partial match on ['leads', 'campaign', campaignId] catches every userEmail
  // variant of the list key (see queryKeys.leads.byCampaign).
  const entries = queryClient.getQueriesData<unknown>({
    queryKey: ['leads', 'campaign', campaignId],
  });
  const targetId = String(leadId);
  for (const [, data] of entries) {
    if (!data) continue;
    // The list cache holds either a bare lead array or a paginated envelope
    // ({ pages: [...] } / { items / leads / data }). Scan arrays of records.
    const arrays: unknown[][] = [];
    if (Array.isArray(data)) {
      arrays.push(data);
    } else if (data && typeof data === 'object') {
      const record = data as Record<string, unknown>;
      for (const key of ['pages', 'items', 'leads', 'data'] as const) {
        if (Array.isArray(record[key])) arrays.push(record[key] as unknown[]);
      }
    }
    for (const arr of arrays) {
      for (const item of arr) {
        if (!item || typeof item !== 'object') continue;
        const record = item as Record<string, unknown>;
        const itemId = record.id ?? record.lead_id ?? record.lvl2_id;
        if (itemId === undefined || itemId === null) continue;
        if (String(itemId) === targetId) {
          const status = typeof record.status === 'string' ? record.status : undefined;
          const substatus =
            typeof record.salesSubstatus === 'string' || record.salesSubstatus === null
              ? (record.salesSubstatus as string | null)
              : typeof record.lead_substatus === 'string' || record.lead_substatus === null
                ? (record.lead_substatus as string | null)
                : undefined;
          if (status) return { status, salesSubstatus: substatus ?? null };
        }
      }
    }
  }
  return undefined;
};

export const useLeadDetail = (campaignId: string, leadId: string, userEmail: string) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: queryKeys.leads.detail(campaignId, leadId),
    queryFn: async () => {
      const fullLead = await appwriteQueryFns.leads.detail({ campaignId, leadId, userEmail });
      if (!fullLead) return fullLead;

      // Reconcile the server's status against the optimistic status the ISR
      // workspace already wrote into the LIST cache. `getPreferredStatus`
      // keeps the most advanced known status — so a refetch whose server value
      // lags behind the user's just-applied change can no longer regress the
      // workspace back to the stale value. Matches the route-level prefetch
      // pattern at src/routes/campaigns/$campaignId/index.tsx:112-128.
      let reconciled = fullLead;
      const listLead = readListLeadStatus(queryClient, campaignId, leadId);
      if (listLead?.status) {
        const preferred = getPreferredStatus(listLead.status, reconciled.status) ?? reconciled.status;
        if (preferred !== reconciled.status) {
          reconciled = { ...reconciled, status: preferred };
        }
      }

      // Defend saved contact fields inside the n8n→DB sync window: a refetch
      // that reads the leads source BEFORE n8n's write-back landed would
      // otherwise bounce the user's just-saved values off the screen.
      return applyPendingIsrWrites(reconciled, campaignId, leadId);
    },
    enabled: !!campaignId && !!leadId && !!userEmail,
    // The n8n webhook backing this query hiccups transiently (cold starts,
    // mid-write reads). The fetcher now THROWS on failure so these retries
    // actually run — 3 attempts with short delays keeps the ISR workspace in
    // its skeleton state through the hiccup instead of flashing the
    // "Lead workspace unavailable" error screen.
    retry: (failureCount, error: any) => {
      if (error?.status >= 400 && error?.status < 500) return false;
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 4000),
    staleTime: 15 * 60 * 1000,      // 15 minutes — reduce n8n-proxy get_lead refetches;
                                    // enrichment still delivers fresh data via the sidebar
                                    // polling that invalidates the storage keys.
    gcTime: 24 * 60 * 60 * 1000,   // 24h — persisted entries stay usable across sessions.
    refetchOnMount: true,           // Only refetches when stale (15 min) — keeps the workspace
                                    // from showing pre-enrichment data on first open but avoids
                                    // unnecessary get_lead calls during idle navigation.
    refetchOnWindowFocus: false,
  });
};
